import React, { useState } from "react";
import api from "../lib/api";
import { X, Loader2, Calendar as CalIcon, Clock, MessageCircle } from "lucide-react";

// Modal to book appointment. On success, shows WhatsApp share popup.
const AppointmentModal = ({ open, patient, onClose, onCreated, doctor }) => {
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    time: "10:00",
    reason: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [waPrompt, setWaPrompt] = useState(null); // { message, phone, appt }

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const res = await api.post("/appointments", { patient_id: patient.id, ...form });
      const message = buildMessage(res.data, patient, doctor);
      onCreated?.(res.data);
      setWaPrompt({ message, phone: patient.phone, appt: res.data });
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Failed to book");
    } finally {
      setBusy(false);
    }
  };

  const closeAll = () => {
    setWaPrompt(null);
    onClose();
    setForm({ date: new Date().toISOString().slice(0, 10), time: "10:00", reason: "", notes: "" });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="font-semibold text-slate-800">Book Appointment</h3>
          <button onClick={closeAll} className="text-slate-400 hover:text-slate-700">
            <X size={18} />
          </button>
        </div>

        {waPrompt ? (
          <div className="px-6 py-6 text-center">
            <div className="w-14 h-14 rounded-full bg-green-100 text-green-600 mx-auto flex items-center justify-center mb-3">
              <MessageCircle size={28} />
            </div>
            <h4 className="font-semibold text-slate-800 mb-1">Appointment Booked</h4>
            <p className="text-sm text-slate-500 mb-5">
              Send confirmation to <span className="font-medium">{patient.name}</span> on WhatsApp?
            </p>
            <div className="bg-slate-50 border border-slate-200 rounded p-3 text-left text-sm text-slate-700 whitespace-pre-line mb-4">
              {waPrompt.message}
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={closeAll} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md">Skip</button>
              <a
                href={waUrl(waPrompt.phone, waPrompt.message)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeAll}
                className="px-4 py-2 text-sm bg-[#25D366] hover:bg-[#1EBA57] text-white rounded-md font-medium inline-flex items-center gap-2"
              >
                <MessageCircle size={14} /> Send on WhatsApp
              </a>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="px-6 py-5 space-y-4">
            <div className="bg-slate-50 rounded-md px-3 py-2 text-sm">
              <span className="text-slate-500">Patient: </span>
              <span className="font-medium text-slate-800">{patient.name}</span>
              <span className="text-slate-400"> • UHID {patient.uhid}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Date</label>
                <div className="relative">
                  <CalIcon size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input type="date" required value={form.date} onChange={(e) => set("date", e.target.value)} className="w-full pl-8 pr-2 py-2 border border-slate-300 rounded-md text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Time</label>
                <div className="relative">
                  <Clock size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input type="time" required value={form.time} onChange={(e) => set("time", e.target.value)} className="w-full pl-8 pr-2 py-2 border border-slate-300 rounded-md text-sm" />
                </div>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Reason</label>
              <input value={form.reason} onChange={(e) => set("reason", e.target.value)} placeholder="e.g. Root canal follow-up" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Notes</label>
              <textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </div>
            {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded">{err}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={closeAll} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md">Cancel</button>
              <button type="submit" disabled={busy} className="px-4 py-2 text-sm bg-[#14B8A6] hover:bg-[#0F8A7D] disabled:opacity-70 text-white rounded-md font-medium flex items-center gap-2">
                {busy && <Loader2 size={14} className="animate-spin" />}
                Book Appointment
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

function buildMessage(appt, patient, doctor) {
  const dr = doctor?.name || "Doctor";
  const clinic = doctor?.clinic_name ? ` (${doctor.clinic_name})` : "";
  return `Hi ${patient.name},

Your appointment with ${dr}${clinic} is confirmed.

Date: ${appt.date}
Time: ${appt.time}${appt.reason ? `\nReason: ${appt.reason}` : ""}

Please arrive 10 minutes early. Reply to reschedule.

Thank you.`;
}

function waUrl(phone, message) {
  // Sanitize phone: keep only digits, prepend country code if missing (assume 91 for India when 10 digits)
  const digits = (phone || "").replace(/\D/g, "");
  const withCC = digits.length === 10 ? `91${digits}` : digits;
  return `https://wa.me/${withCC}?text=${encodeURIComponent(message)}`;
}

export default AppointmentModal;
